// src/errors/ErrorPage.tsx

import { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    AlertTriangle,
    ArrowLeft,
    FileSearch,
    Headphones,
    Info,
    LayoutDashboard,
    LoaderCircle,
    LockKeyhole,
    RefreshCw,
    SearchX,
    ServerCrash,
} from "lucide-react";

export type ErrorPageVariant =
    | "server"
    | "warning"
    | "not-found"
    | "forbidden"
    | "empty"
    | "redirect";

type SecondaryActionType =
    | "back"
    | "reload"
    | "link"
    | "none";

interface ErrorPageProps {
    variant?: ErrorPageVariant;

    title: string;
    message: string;

    statusLabel?: string;
    statusCode?: string | number;

    helperText?: ReactNode;

    actionLabel?: string;
    redirectTo?: string;

    secondaryActionLabel?: string;
    secondaryActionType?: SecondaryActionType;
    secondaryRedirectTo?: string;

    showSupport?: boolean;
}

const styles: Record<
    ErrorPageVariant,
    {
        icon: React.ComponentType<{ className?: string }>;
        iconClass: string;
        iconBackground: string;
        statusDot: string;
        codeClass: string;
        illustrationGlow: string;
    }
> = {
    server: {
        icon: ServerCrash,
        iconClass: "text-red-500 dark:text-red-400",
        iconBackground:
            "bg-red-50 border-red-100 dark:bg-red-950/30 dark:border-red-900/50",
        statusDot: "bg-red-500",
        codeClass:
            "bg-red-50 text-red-600 border-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50",
        illustrationGlow: "bg-red-100/60 dark:bg-red-900/10",
    },

    warning: {
        icon: AlertTriangle,
        iconClass: "text-amber-500 dark:text-amber-400",
        iconBackground:
            "bg-amber-50 border-amber-100 dark:bg-amber-950/30 dark:border-amber-900/50",
        statusDot: "bg-amber-500",
        codeClass:
            "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/50",
        illustrationGlow: "bg-amber-100/60 dark:bg-amber-900/10",
    },

    "not-found": {
        icon: SearchX,
        iconClass:
            "text-[var(--kmstack-green)] dark:text-green-400",
        iconBackground:
            "bg-green-50 border-green-100 dark:bg-green-950/30 dark:border-green-900/50",
        statusDot: "bg-[var(--kmstack-green)]",
        codeClass:
            "bg-red-50 text-red-600 border-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50",
        illustrationGlow:
            "bg-green-100/60 dark:bg-green-900/10",
    },

    forbidden: {
        icon: LockKeyhole,
        iconClass: "text-red-500 dark:text-red-400",
        iconBackground:
            "bg-red-50 border-red-100 dark:bg-red-950/30 dark:border-red-900/50",
        statusDot: "bg-red-500",
        codeClass:
            "bg-red-50 text-red-600 border-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50",
        illustrationGlow: "bg-red-100/60 dark:bg-red-900/10",
    },

    empty: {
        icon: FileSearch,
        iconClass:
            "text-[var(--kmstack-green)] dark:text-green-400",
        iconBackground:
            "bg-green-50 border-green-100 dark:bg-green-950/30 dark:border-green-900/50",
        statusDot: "bg-[var(--kmstack-green)]",
        codeClass:
            "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
        illustrationGlow:
            "bg-green-100/60 dark:bg-green-900/10",
    },

    redirect: {
        icon: LoaderCircle,
        iconClass:
            "text-[var(--kmstack-green)] dark:text-green-400 animate-spin",
        iconBackground:
            "bg-green-50 border-green-100 dark:bg-green-950/30 dark:border-green-900/50",
        statusDot: "bg-[var(--kmstack-green)]",
        codeClass:
            "bg-green-50 text-green-700 border-green-100 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900/50",
        illustrationGlow:
            "bg-green-100/60 dark:bg-green-900/10",
    },
};

export default function ErrorPage({
    variant = "server",
    title,
    message,

    statusLabel = "System status",
    statusCode,

    helperText,

    actionLabel = "Go to Dashboard",
    redirectTo = "/",

    secondaryActionLabel,
    secondaryActionType = "none",
    secondaryRedirectTo,

    showSupport = false,
}: ErrorPageProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const style = styles[variant];
    const Icon = style.icon;

    const handleSecondaryAction = () => {
        console.log("reload called")
        switch (secondaryActionType) {
            case "back":
                navigate(-1);
                break;

            case "reload":
                window.location.reload();
                break;

            default:
                break;
        }
    };

    const renderSecondaryAction = () => {
        console.log("secondaryActionType", secondaryActionType)
        if (
            !secondaryActionLabel ||
            secondaryActionType === "none"
        ) {
            return null;
        }

        const classes = `
            inline-flex
            h-11
            min-w-[150px]
            items-center
            justify-center
            gap-2
            rounded-lg
            border
            border-gray-200
            bg-white
            px-5
            text-sm
            font-semibold
            text-gray-700
            shadow-sm
            transition-all
            duration-200
            hover:border-gray-300
            hover:bg-gray-50
            focus:outline-none
            focus:ring-2
            focus:ring-[var(--kmstack-green)]/20
            dark:border-gray-700
            dark:bg-[var(--app-darkSurface)]
            dark:text-gray-200
            dark:hover:bg-[var(--app-darkSurfaceHover)]
        `;

        if (
            secondaryActionType === "link" &&
            secondaryRedirectTo
        ) {
            return (
                <Link
                    to={secondaryRedirectTo}
                    className={classes}
                >
                    <ArrowLeft className="h-4 w-4" />
                    {secondaryActionLabel}
                </Link>
            );
        }

        return (
            <button
                type="button"
                onClick={() => { handleSecondaryAction() }}
                className={classes}
            >
                {secondaryActionType === "reload" ? (
                    <RefreshCw className="h-4 w-4" />
                ) : (
                    <ArrowLeft className="h-4 w-4" />
                )}

                {secondaryActionLabel}
            </button>
        );
    };

    return (
        <main
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-[var(--app-surfaceNeutral)]
                px-4
                py-6
                dark:bg-[var(--app-darkSurfaceDeep)]
                sm:px-6
                lg:px-8
            "
        >
            {/* Decorative background */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute left-1/2 top-[35%] h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--kmstack-green)]/[0.025] blur-3xl" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-gray-800" />
            </div>

            <div className="relative mx-auto flex min-h-[calc(100vh-3rem)] max-w-7xl flex-col">
                {/* Brand */}
                <header className="flex h-14 items-center">
                    <Link to="/" className="inline-flex items-center gap-2.5 text-gray-900 dark:text-white">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--kmstack-green)] text-[11px] font-bold tracking-tight text-white shadow-sm">
                            KM
                        </div>
                        <span className="text-base font-semibold tracking-tight">
                            KMStack
                        </span>
                    </Link>
                </header>

                {/* Main content */}
                <div className="flex flex-1 items-center justify-center py-8 sm:py-12">
                    <section className="relative w-full max-w-[760px] overflow-hidden rounded-[22px] border border-gray-200/90 bg-white
                            shadow-[0_20px_60px_-35px_var(--app-effect15x23x42a0_25)] dark:border-gray-800  dark:bg-[var(--app-darkPanel)] dark:shadow-none">
                        {/* subtle top accent */}
                        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--kmstack-green)]/40 to-transparent" />
                        <div className="px-6 py-10 sm:px-12 sm:py-12 lg:px-16">
                            {/* Illustration */}
                            <div className="mb-7 flex justify-center">
                                <div className="relative flex h-36 w-48 items-center justify-center">
                                    {/* glow */}
                                    <div className={`absolute h-28 w-28 rounded-full blur-2xl ${style.illustrationGlow}`} />
                                    {/* decorative grid */}
                                    <div aria-hidden="true" className="absolute right-0 top-1 grid grid-cols-5 gap-1.5 opacity-40" >
                                        {Array.from({ length: 20 }).map((_, index) => (
                                            <span key={index} className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"
                                            />
                                        ))}
                                    </div>

                                    {/* faux product window */}
                                    <div className="absolute left-3 top-6 h-24 w-36 rounded-xl border border-gray-200 bg-white/90 shadow-sm dark:border-gray-700 dark:bg-[var(--app-darkSurface)]">
                                        <div className="flex h-7 items-center gap-1.5 border-b border-gray-100 px-3 dark:border-gray-800">
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
                                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
                                        </div>

                                        <div className="space-y-2.5 px-4 py-4">
                                            <div className="h-2 w-16 rounded bg-gray-100 dark:bg-gray-800" />
                                            <div className="h-2 w-24 rounded bg-gray-100 dark:bg-gray-800" />
                                            <div className="h-2 w-20 rounded bg-gray-100 dark:bg-gray-800" />
                                        </div>
                                    </div>

                                    {/* status icon */}
                                    <div className={`absolute bottom-1 right-3 flex h-[72px] w-[72px] items-center justify-center rounded-2xl border shadow-lg shadow-black/5 ${style.iconBackground}`}>
                                        <Icon className={`h-8 w-8 ${style.iconClass}`} />
                                    </div>
                                </div>
                            </div>

                            {/* Status */}
                            <div className="mb-4 flex justify-center">
                                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-[var(--app-darkSurface)] dark:text-gray-300" >
                                    <span className={`h-2 w-2 rounded-full ${ style.statusDot }`} />
                                    {statusLabel}
                                </div>
                            </div>

                            {/* Copy */}
                            <div className="mx-auto max-w-xl text-center">
                                <h1 className="text-[30px] font-bold tracking-[-0.03em] text-gray-950 dark:text-white sm:text-[34px]">
                                    {title}
                                </h1>

                                <p className="mx-auto mt-3 max-w-lg text-[15px] leading-6 text-gray-500 dark:text-gray-400">
                                    {message}
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row">
                                <button
                                    type="button"
                                    onClick={() => {
                                        queryClient.invalidateQueries({ queryKey: ['sessionContext'] });
                                        navigate(redirectTo, { replace: true });
                                    }}
                                    className="inline-flex h-11 min-w-[180px] items-center justify-center gap-2 rounded-lg bg-[var(--kmstack-green)] px-5 
                                            text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-[1px] hover:bg-[var(--kmstack-dark-green)] 
                                            hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--kmstack-green)]/30"
                                >
                                    <LayoutDashboard className="h-4 w-4" />
                                    {actionLabel}
                                </button>

                                {renderSecondaryAction()}
                            </div>

                            {/* Footer information */}
                            {(helperText ||
                                showSupport ||
                                statusCode) && (
                                    <div className="mx-auto mt-8 max-w-lg border-t border-gray-100 pt-6 dark:border-gray-800">
                                        {helperText && (
                                            <div className="flex items-center justify-center gap-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                                                    <Info className="h-4 w-4" />
                                                </span>
                                                <span>
                                                    {helperText}
                                                </span>
                                            </div>
                                        )}

                                        {showSupport && (
                                            <div className="mt-4 flex items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                                <Headphones className="h-4 w-4" />

                                                <span>
                                                    If the problem continues, {" "}
                                                    <Link to="/support" className="font-medium text-[var(--kmstack-green)] hover:underline">
                                                        contact support
                                                    </Link>
                                                    .
                                                </span>
                                            </div>
                                        )}

                                        {statusCode && (
                                            <div className="mt-5 flex items-center justify-center gap-2 text-xs text-gray-400">
                                                <span>
                                                    {variant === "redirect" ? "Status code" : variant === "empty" ? "Status" : "Error code"}
                                                </span>

                                                <span className={`rounded-md border px-2.5 py-1 font-semibold ${style.codeClass}`}>
                                                    {statusCode}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <footer className="flex min-h-12 items-center justify-center text-xs text-gray-400 dark:text-gray-600">
                    KMStack · Secure business operations
                </footer>
            </div>
        </main>
    );
}
